import { useState } from 'react';
import { Alert, Box, Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { HMIFieldRule, HMITemplate } from '../../types';
import { addRule, deleteRule, updateRule } from '../../layoutUtils';
import { describeRule } from '../../rules';
import RuleEditorDialog, { RuleDraft } from './RuleEditorDialog';

export interface RulesPanelProps {
  template: HMITemplate;
  readOnly?: boolean;
  onChange: (next: HMITemplate) => void;
}

type PendingRule = { kind: 'add' } | { kind: 'edit'; rule: HMIFieldRule } | null;

/**
 * Template-wide list of cross-field dependency/validation rules (see
 * `HMIFieldRule`), editable without JSON. Rules can reference fields across
 * any tab/subtab/section, since dependencies (e.g. "pressure must stay below
 * temperature") commonly span more than one section.
 */
function RulesPanel({ template, readOnly, onChange }: RulesPanelProps) {
  const [pending, setPending] = useState<PendingRule>(null);
  const { fields } = template;
  const rules = template.rules ?? [];

  const handleSave = (draft: RuleDraft) => {
    if (pending?.kind === 'add') {
      onChange(addRule(template, draft));
    } else if (pending?.kind === 'edit') {
      onChange(updateRule(template, pending.rule.id, draft));
    }
    setPending(null);
  };

  const handleDelete = (ruleId: string) => {
    onChange(deleteRule(template, ruleId));
    setPending(null);
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Define how fields depend on each other - value comparisons ("B must be greater than A"),
        mutual exclusivity ("if A is set, B can't be"), or conditional constraints ("if temp &gt;
        10 then pressure &lt; 20"). Violations block the commit with the message shown below.
      </Typography>

      {fields.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Add fields to a section first - rules compare field values to each other.
        </Alert>
      )}

      <Stack spacing={1}>
        {rules.map((rule) => (
          <Paper key={rule.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2">{rule.description || describeRule(rule, fields)}</Typography>
              {rule.description && (
                <Typography variant="caption" color="text.secondary">
                  {describeRule(rule, fields)}
                </Typography>
              )}
            </Box>
            {!readOnly && (
              <Stack direction="row" spacing={0}>
                <Tooltip title="Edit rule">
                  <IconButton size="small" onClick={() => setPending({ kind: 'edit', rule })}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete rule">
                  <IconButton size="small" onClick={() => handleDelete(rule.id)}>
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Paper>
        ))}
        {rules.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No rules yet.
          </Typography>
        )}
      </Stack>

      {!readOnly && (
        <Button
          sx={{ mt: 2 }}
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={fields.length === 0}
          onClick={() => setPending({ kind: 'add' })}
        >
          Add rule
        </Button>
      )}

      {pending && (
        <RuleEditorDialog
          rule={pending.kind === 'edit' ? pending.rule : undefined}
          fields={fields}
          onCancel={() => setPending(null)}
          onSave={handleSave}
          onDelete={pending.kind === 'edit' ? () => handleDelete(pending.rule.id) : undefined}
        />
      )}
    </Box>
  );
}

export default RulesPanel;
